import { ComponentConfig, WithId, WithPuckProps } from "@measured/puck";

export interface FlexProps {
  options?: {
    desktop?: {
      justifyContent?: "start" | "center" | "end";
      alignItems?: "center" | "start" | "end";
      direction?: "row" | "column";
      gap?: number;
      wrap?: "wrap" | "nowrap";
    };
    tablet?: {
      justifyContent?: "start" | "center" | "end";
      alignItems?: "center" | "start" | "end";
      direction?: "row" | "column";
      gap?: number;
      wrap?: "wrap" | "nowrap";
    };
    mobile?: {
      justifyContent?: "start" | "center" | "end";
      alignItems?: "center" | "start" | "end";
      direction?: "row" | "column";
      gap?: number;
      wrap?: "wrap" | "nowrap";
    };
  };
}

const Flex = (props: WithId<WithPuckProps<FlexProps>>) => {
  const { options, id } = props;
  const uniqueClass = `flex-responsive-${id}`;

  return (
    <div>
      <style>{`
          @media only screen and (max-width: 743px) {
            .${uniqueClass} {
              display: flex;
              justify-content: ${options?.mobile?.justifyContent ?? "start"};
              align-items: ${options?.mobile?.alignItems ?? "center"};
              flex-direction: ${options?.mobile?.direction ?? "column"};
              gap: ${options?.mobile?.gap ?? 0}px;
              flex-wrap: ${options?.mobile?.wrap ?? "wrap"};
            }
          }
          @media only screen and (min-width: 744px) and (max-width: 1023px) {
            .${uniqueClass} {
              display: flex;
              justify-content: ${options?.tablet?.justifyContent ?? "start"};
              align-items: ${options?.tablet?.alignItems ?? "center"};
              flex-direction: ${options?.tablet?.direction ?? "column"};
              gap: ${options?.tablet?.gap ?? 0}px;
              flex-wrap: ${options?.tablet?.wrap ?? "wrap"};
            }
          }
          @media only screen and (min-width: 1024px) {
            .${uniqueClass} {
              display: flex;
              justify-content: ${options?.desktop?.justifyContent ?? "start"};
              align-items: ${options?.desktop?.alignItems ?? "center"};
              flex-direction: ${options?.desktop?.direction ?? "column"};
              gap: ${options?.desktop?.gap ?? 0}px;
              flex-wrap: ${options?.desktop?.wrap ?? "wrap"};
            }
          }
        `}</style>
      {props.puck.renderDropZone({
        zone: "my-flex-zone",
        className: uniqueClass,
      })}
    </div>
  );
};

export const PuckFlex: ComponentConfig<FlexProps> = {
  label: "Flex",
  fields: {
    options: {
      type: "object",
      label: "Options",
      objectFields: {
        desktop: {
          type: "object",
          label: "Desktop",
          objectFields: {
            direction: {
              type: "select",
              label: "Direction",
              options: [
                { label: "Row", value: "row" },
                { label: "Column", value: "column" },
              ],
            },
            alignItems: {
              type: "select",
              label: "Align Items",
              options: [
                { label: "Center", value: "center" },
                { label: "Start", value: "start" },
                { label: "End", value: "end" },
              ],
            },
            justifyContent: {
              type: "select",
              label: "Justify Content",
              options: [
                { label: "Center", value: "center" },
                { label: "Start", value: "start" },
                { label: "End", value: "end" },
              ],
            },
            wrap: {
              type: "select",
              label: "Wrap",
              options: [
                { label: "Wrap", value: "wrap" },
                { label: "No Wrap", value: "nowrap" },
              ],
            },
            gap: { type: "number", label: "Gap", min: 0 },
          },
        },
        tablet: {
          type: "object",
          label: "Tablet",
          objectFields: {
            direction: {
              type: "select",
              label: "Direction",
              options: [
                { label: "Row", value: "row" },
                { label: "Column", value: "column" },
              ],
            },
            alignItems: {
              type: "select",
              label: "Align Items",
              options: [
                { label: "Center", value: "center" },
                { label: "Start", value: "start" },
                { label: "End", value: "end" },
              ],
            },
            justifyContent: {
              type: "select",
              label: "Justify Content",
              options: [
                { label: "Center", value: "center" },
                { label: "Start", value: "start" },
                { label: "End", value: "end" },
              ],
            },
            wrap: {
              type: "select",
              label: "Wrap",
              options: [
                { label: "Wrap", value: "wrap" },
                { label: "No Wrap", value: "nowrap" },
              ],
            },
            gap: { type: "number", label: "Gap", min: 0 },
          },
        },
        mobile: {
          type: "object",
          label: "Mobile",
          objectFields: {
            direction: {
              type: "select",
              label: "Direction",
              options: [
                { label: "Row", value: "row" },
                { label: "Column", value: "column" },
              ],
            },
            alignItems: {
              type: "select",
              label: "Align Items",
              options: [
                { label: "Center", value: "center" },
                { label: "Start", value: "start" },
                { label: "End", value: "end" },
              ],
            },
            justifyContent: {
              type: "select",
              label: "Justify Content",
              options: [
                { label: "Center", value: "center" },
                { label: "Start", value: "start" },
                { label: "End", value: "end" },
              ],
            },
            wrap: {
              type: "select",
              label: "Wrap",
              options: [
                { label: "Wrap", value: "wrap" },
                { label: "No Wrap", value: "nowrap" },
              ],
            },
            gap: { type: "number", label: "Gap", min: 0 },
          },
        },
      },
    },
  },
  defaultProps: {
    options: {
      desktop: {
        alignItems: "center",
        direction: "column",
        gap: 12,
        justifyContent: "center",
        wrap: "wrap",
      },
      tablet: {
        alignItems: "center",
        direction: "column",
        gap: 12,
        justifyContent: "center",
        wrap: "wrap",
      },
      mobile: {
        alignItems: "center",
        direction: "column",
        gap: 12,
        justifyContent: "center",
        wrap: "wrap",
      },
    },
  },
  render: (props) => <Flex {...props} />,
};
